import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Clock, ArrowLeft, CheckCircle2, Mail } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { toast } from "sonner";

export default function CourseComingSoon() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // { already_subscribed, email }

  useEffect(() => {
    api
      .get(`/courses/${slug}`)
      .then((r) => {
        setCourse(r.data);
        // If course is NOT coming-soon, this page shouldn't be here.
        if (!r.data.is_coming_soon) {
          navigate(`/courses/${slug}`, { replace: true });
        }
      })
      .catch((e) => {
        console.error("Failed to load course", e);
        setError(e.response?.status === 404 ? "Course not found" : "Failed to load course");
      })
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  useEffect(() => {
    if (user?.email) setEmail(user.email);
  }, [user]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      toast.error("Please enter a valid email");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post(`/courses/${slug}/waitlist`, { email });
      setSubmitted({ already_subscribed: !!r.data.already_subscribed, email });
    } catch (err) {
      console.error("Waitlist join failed", err);
      toast.error(err.response?.data?.detail || "Could not join waitlist");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-fc-navy min-h-screen text-white">
        <Navbar />
        <div className="pt-32 max-w-5xl mx-auto px-6"><p className="text-white/50">Loading…</p></div>
      </div>
    );
  }
  if (error || !course) {
    return (
      <div className="bg-fc-navy min-h-screen text-white">
        <Navbar />
        <div className="pt-32 max-w-5xl mx-auto px-6">
          <p className="text-white/70 mb-6">{error || "Course not found."}</p>
          <Link to="/courses" className="btn-fc-red"><ArrowLeft size={14} /> Back to catalog</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-fc-navy min-h-screen text-white">
      <Navbar />
      <main className="pt-28 pb-24" data-testid={`course-coming-soon-${slug}`}>
        <section className="max-w-3xl mx-auto px-6 lg:px-10">
          <Link
            to="/courses"
            className="inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase font-bold text-white/55 hover:text-white mb-8 transition-colors"
            data-testid="back-to-catalog"
          >
            <ArrowLeft size={12} /> Back to catalog
          </Link>

          {/* Hero */}
          <div className="mb-8">
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1 text-[10px] tracking-[0.22em] uppercase font-black rounded-full border border-amber-400/60 bg-amber-500/10 text-amber-300 mb-5"
              data-testid="coming-soon-pill"
            >
              <Clock size={11} /> Coming Soon
            </span>
            <h1 className="fc-h-section text-4xl sm:text-5xl lg:text-6xl mb-4 leading-[1.05]">
              {course.title}
            </h1>
            {course.subtitle && (
              <p className="text-white/65 text-lg max-w-2xl">{course.subtitle}</p>
            )}
            {course.instructor && (
              <div className="flex items-center gap-3 mt-6">
                {course.instructor.avatar_url && (
                  <img
                    src={course.instructor.avatar_url}
                    alt={course.instructor.name}
                    className="w-10 h-10 rounded-full object-cover border border-white/10"
                  />
                )}
                <div>
                  <p className="text-xs tracking-[0.16em] uppercase text-white/45">Instructor</p>
                  <p className="text-sm font-bold">{course.instructor.name}</p>
                </div>
              </div>
            )}
          </div>

          {/* Capture form / confirmation */}
          <div className="bg-[#060A24] border border-white/10 p-8 sm:p-10" data-testid="waitlist-card">
            {submitted ? (
              <div className="text-center" data-testid="waitlist-success">
                <CheckCircle2 size={42} className="mx-auto text-emerald-400 mb-4" />
                <h2 className="text-2xl font-black mb-2 tracking-tight">
                  {submitted.already_subscribed ? "You're already on the list." : "You're on the list."}
                </h2>
                <p className="text-white/60 text-sm max-w-md mx-auto">
                  {submitted.already_subscribed
                    ? `We'll email ${submitted.email} the moment ${course.title} goes live.`
                    : <>We'll email <span className="text-white font-bold">{submitted.email}</span> the moment <span className="text-white font-bold">{course.title}</span> goes live.</>}
                </p>
                <div className="mt-8">
                  <Link to="/courses" className="btn-fc-red inline-flex" data-testid="back-to-catalog-cta">
                    Browse live courses
                  </Link>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-5">
                  <Mail size={20} className="text-amber-300" />
                  <h2 className="text-xl font-black tracking-tight">Get notified when it launches</h2>
                </div>
                <p className="text-white/55 text-sm mb-6 max-w-lg">
                  Drop your email and we'll send you a one-time message the moment
                  <span className="text-white font-bold"> {course.title}</span> is live in the academy.
                </p>
                <form onSubmit={onSubmit} className="flex flex-col sm:flex-row gap-3" data-testid="waitlist-form">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@dealership.com"
                    required
                    className="flex-1 bg-fc-navy border border-white/15 focus:border-amber-400 outline-none text-white px-4 py-3 text-sm transition-colors"
                    data-testid="waitlist-email-input"
                    disabled={submitting}
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-3 text-xs tracking-[0.22em] uppercase font-black bg-amber-500 hover:bg-amber-400 text-fc-navy transition-colors disabled:opacity-50"
                    data-testid="waitlist-submit"
                  >
                    {submitting ? "Saving…" : "Join Waitlist"}
                  </button>
                </form>
              </>
            )}
          </div>

          {/* What you'll learn — preview of value */}
          {course.what_you_will_learn?.length > 0 && (
            <div className="mt-12">
              <p className="fc-overline text-[10px] mb-3">A preview of what's inside</p>
              <ul className="space-y-2.5">
                {course.what_you_will_learn.slice(0, 4).map((line, i) => (
                  <li key={i} className="flex items-start gap-3 text-white/70 text-sm">
                    <CheckCircle2 size={14} className="text-amber-400 mt-1 flex-shrink-0" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Footnote — no per-course pricing */}
          <p className="mt-12 pt-8 border-t border-white/5 text-xs text-white/40 text-center">
            All courses are included with your Freddy Compute Academy membership — no per-course charges.
          </p>
        </section>
      </main>
      <Footer />
    </div>
  );
}
